import _ from 'lodash';
import Vue from 'vue';
import FormError from './FormErrors';
import FormField from './FormField';

export default class FormGroup extends FormError {
  constructor(fields) {
    super();
    this.errors = [];
    this.fields = {};

    if (!fields || !Object.keys(fields).length) {
      return new Error('One field is required to create a form group');
    }

    Object.keys(fields)
      .forEach((key) => {
        this.fields[key] = new FormField(fields[key]);
      });
  }

  getRawValue(camelCase) {
    const rawValue = {};
    Object.keys(this.fields)
      .forEach((fieldName) => {
        const field = this.fields[fieldName];

        if (field.ignoreOnGetRawValue) {
          return;
        }

        if (field.ignoredOnGetRawValueIfMatch && this.ignoredOnGetRawValueIfMatch(field)) {
          return;
        }

        if (field.ignoreIfNot && !this.ignoreIfNotHaveMatch(field)) {
          field.value = undefined;
        }

        let { value } = field;
        if (field.cpf) {
          value = field.clearValue();
        }
        if (!camelCase) {
          rawValue[_.snakeCase(fieldName)] = value;
        } else {
          rawValue[fieldName] = value;
        }
      });

    return rawValue;
  }

  checkIfFieldMatchValue(valuesToMatch, value) {
    return (
      (valuesToMatch instanceof Array && valuesToMatch.indexOf(value) > -1)
      || (!(valuesToMatch instanceof Array) && value === valuesToMatch)
    );
  }

  ignoreIfNotHaveMatch(field) {
    let match = true;
    const keys = Object.keys(field.ignoreIfNot);
    for (let i = 0; i < keys.length; i += 1) {
      if (!this.checkIfFieldMatchValue(field.ignoreIfNot[keys[i]], this.getValue(keys[i]))) {
        match = false;
        break;
      }
    }
    return match;
  }

  validateForm() {
    this.errors = [];
    Object.keys(this.fields)
      .forEach((fieldName) => {
        this.validateField(fieldName);
      });
    return !this.errors.length;
  }

  validateField(fieldName) {
    const field = this.fields[fieldName];
    if (field.ignoreIfNot && !this.ignoreIfNotHaveMatch(field)) {
      field.errors = [];
      field.valid = true;
      return;
    }

    if (field.requiredIfMatch) {
      field.required = this.requiredIfMatch(field);
    }

    if (!field.validateField()) {
      this.errors.push(fieldName);
    }

    if (field.matchTo && field.value !== this.fields[field.matchTo].value) {
      this.errors.push(fieldName);
      field.errors.push(this.matchToError(field.alias, this.fields[field.matchTo].alias));
    }

    field.valid = field.errors.length === 0;
  }

  addValues(fields) {
    Object.keys(fields)
      .forEach((fieldName) => {
        if (this.fields[fieldName]) {
          Vue.set(this.fields[fieldName], 'value', fields[fieldName]);
          this.fields[fieldName].validateField(true);
          if (this.fields[fieldName].onAddValueReplicateTo) {
            this.replicateValueTo(
              _.get(this.fields[fieldName], this.fields[fieldName].onAddValueReplicateTo.path),
              this.fields[fieldName].onAddValueReplicateTo.to,
            );
          }
        }
      });
  }

  replicateValueTo(value, to) {
    const field = _.get(this.fields, to);
    Vue.set(field, 'value', value);
  }

  addErrors(fields) {
    Object.keys(fields)
      .forEach((fieldName) => {
        this.fields[fieldName].errors.push(fields[fieldName]);
        this.errors.push(fieldName);
      });
  }

  get(key) {
    return this.fields[key];
  }

  getValue(key) {
    return this.fields[key].value;
  }

  clearValues(fields) {
    fields
      .forEach((fieldName) => {
        if (this.fields[fieldName]) {
          this.fields[fieldName].value = null;
        }
      });
  }

  clearValuesExcept(fields) {
    Object.keys(this.fields)
      .forEach((fieldName) => {
        if (fields.indexOf(fieldName) < 0) {
          this.fields[fieldName].value = null;
        }
      });
  }

  ignoredOnGetRawValueIfMatch(fieldParam) {
    const field = fieldParam;
    let ignored = true;
    const rules = field.ignoredOnGetRawValueIfMatch;
    Object.values(rules).forEach((rule) => {
      const fieldName = Object.keys(rule)[0];
      switch (rule[fieldName].condition) {
        case '!':
          if (rule.condition === '||') {
            ignored = ignored || !rule[fieldName].values.includes(this.fields[fieldName].value);
          } else {
            ignored = ignored && !rule[fieldName].values.includes(this.fields[fieldName].value);
          }
          break;
        default:
          if (rule.condition === '||') {
            ignored = ignored || rule[fieldName].values.includes(this.fields[fieldName].value);
          } else {
            ignored = ignored && rule[fieldName].values.includes(this.fields[fieldName].value);
          }
      }
    });

    return ignored;
  }

  requiredIfMatch(fieldParam) {
    const field = fieldParam;
    let { required } = field;
    const rules = field.requiredIfMatch;
    Object.keys(rules)
      .forEach((fieldName) => {
        const rule = rules[fieldName];
        switch (rule.condition) {
          case '!':
            required = true;
            rule.values
              .forEach((value) => {
                if (value === this.fields[fieldName].value) {
                  required = false;
                }
              });
            break;
          default:
            required = false;
            rule.values
              .forEach((value) => {
                if (value === this.fields[fieldName].value) {
                  required = true;
                }
              });
        }
      });
    return required;
  }
}
